import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Box, Grid, Card, CardActionArea, CardContent, Typography, TextField, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, CircularProgress,
    Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
    Select, FormControl, InputLabel, Tooltip, Tabs, Tab,
} from '@mui/material';
import {
    Search, Add, ArrowBack, OpenInNew, PlayCircleOutline, Refresh,
    PushPin, PushPinOutlined, DescriptionOutlined, UploadFile, Download,
    EditOutlined, DeleteOutline, LinkOutlined, ArrowUpward, ArrowDownward,
    LinkOutlined as LinkDeVideo, VideoFile, PlayArrow, Star, StarBorder,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import CentralDeAjuda from '../../components/CentralDeAjuda';
import { useSelector } from 'react-redux';
import { hasAdminProfile } from '../../utils/profileRole';
import { podeDescer, podeSubir, reordenarFixados } from '../../utils/ordemDosFixados';
import { PERMISSAO_EDITAR, PERMISSAO_FIXAR } from '../../utils/permissoesDaBase';
import {
    listVideos, listDocumentosDaBase, createDocument, updateDocument,
    uploadDocument, downloadDocument, deleteDocument,
    limitesDaBase, urlDoArquivoDoMaterial,
    favoritarMaterial, desfavoritarMaterial,
} from '../../services/aiDocumentsService';
import {
    EXTENSOES_DE_DOCUMENTO, EXTENSOES_DE_VIDEO, aceitar,
    ehVideoEnviado, recusaDoArquivo,
} from '../../utils/arquivoDaBase';

/**
 * A área de vídeos da base de conhecimento (item 11, ponto 10d).
 *
 * ## O que esta tela é, e o que ela não é
 *
 * Ela é a **mesma base de conhecimento, recortada nos vídeos**. Não é uma
 * segunda base: o vídeo é um documento que tem endereço de vídeo, e por isso
 * já nasce com categoria, tags, alcance e -- o que importa -- indexado no
 * Qdrant e respondível pelo ChatOrby. Uma base própria de vídeos precisaria
 * de uma segunda indexação para o Orby achá-los, que é justamente o que o
 * pedido quer evitar.
 *
 * ## Por que uma página separada, e não uma aba na tela existente
 *
 * A `KnowledgeBasePage` tem mais de 2.400 linhas e é a tela por onde a base
 * inteira é administrada hoje. O pedido fala em "área específica para
 * vídeos", e uma página nova entrega isso sem tocar em nada que já está no
 * ar. As duas compartilham o serviço, não o componente.
 *
 * ## A permissão é PRÓPRIA desde 03/11/2026
 *
 * Era a mesma de `/ia-base-conhecimento`, de propósito: uma permissão nova
 * exige entrar em `PermissionService`, na tela de permissões E num backfill no
 * banco -- e, se qualquer um dos três faltar, o item não aparece no menu de
 * quem deveria vê-lo.
 *
 * O pedido da Gisele desfez essa escolha com razão: "Perfil Operador: não
 * apresentar a opção de IA/Base de Conhecimento" e "disponibilizar somente
 * Vídeos e Treinamentos". Enquanto fossem a mesma permissão, dar uma tela era
 * dar as duas. Hoje são `/ia-base-conhecimento/videos` e
 * `#editar-material-da-base` -- ver `utils/permissoesDaBase`.
 *
 * ## Vídeo é link OU arquivo
 *
 * Desde 03/11/2026 ("também teremos que ter como fazer upload e não somente
 * leitura de youtube ou da plataforma que seja"). As duas origens viram o
 * mesmo material; o que muda é o cartão, porque o enviado não tem endereço
 * para abrir numa aba. Ver `utils/arquivoDaBase`.
 */

const SEM_CATEGORIA = 'Sem categoria';

/**
 * As duas abas, e o vocabulário de cada uma.
 *
 * Pedido da Gisele (19/10), esclarecido pelo dono do produto em 20/10: "na
 * página de vídeos e treinamentos deverá ter uma aba somente para vídeos e
 * outra para documentos que iremos subir".
 *
 * Os textos moram aqui, num mapa, e não em `aba === 'video' ? ... : ...`
 * espalhado pela tela: são sete textos por aba, e cada ternário solto seria
 * mais um lugar onde uma delas fica com a palavra da outra ("nenhum vídeo
 * cadastrado" na aba de documentos).
 *
 * `vazio` e `semFiltro` são mensagens DIFERENTES de propósito — trocar uma pela
 * outra manda a pessoa procurar um filtro que não é a causa, ou faz parecer que
 * a base esvaziou.
 */
const ABAS = {
    video: {
        titulo: 'Vídeos',
        vazio: 'Nenhum vídeo cadastrado ainda.',
        semFiltro: 'Nenhum vídeo com os filtros atuais.',
        falhou: 'Não foi possível carregar os vídeos. Tente recarregar.',
        acao: 'Adicionar vídeo',
        busca: 'Buscar por título, categoria ou palavra-chave',
    },
    documento: {
        titulo: 'Documentos',
        vazio: 'Nenhum documento cadastrado ainda.',
        semFiltro: 'Nenhum documento com os filtros atuais.',
        falhou: 'Não foi possível carregar os documentos. Tente recarregar.',
        acao: 'Enviar documento',
        busca: 'Buscar por título, categoria ou palavra-chave',
    },
};

/**
 * A MESMA regra de ordem do servidor: fixados primeiro, depois os favoritos
 * de quem está olhando, depois a ordem de sempre, depois o título.
 *
 * Existe só para reposicionar o cartão na hora em que alguém fixa ou
 * favorita, sem recarregar a página inteira. Quem ordena de verdade é o
 * servidor (`AIDocument::scopeOrdered`) — esta é a cópia que mantém a tela
 * coerente entre uma carga e a seguinte, e por isso precisa concordar com ele.
 *
 * ## O favorito vem DEPOIS do fixado
 *
 * É a mesma decisão do servidor, e ela tem motivo: o fixado é o que o
 * Admin/Líder escolheu para a equipe. Se o favorito passasse na frente, o
 * operador empurraria para baixo — na própria tela — o que a liderança
 * destacou. É "desfixar" por outro caminho, e é justamente o que o pedido da
 * Gisele proíbe.
 */
const ordenarComFixadosPrimeiro = (materiais) => [...materiais].sort((a, b) => {
    const fixado = Number(Boolean(b.fixado)) - Number(Boolean(a.fixado));
    if (fixado !== 0) return fixado;

    const favorito = Number(Boolean(b.favorito)) - Number(Boolean(a.favorito));
    if (favorito !== 0) return favorito;

    const ordem = (a.order ?? 0) - (b.order ?? 0);
    if (ordem !== 0) return ordem;

    return String(a.title || '').localeCompare(String(b.title || ''), 'pt-BR');
});

const VideosDaBasePage = () => {
    const { theme } = useContext(ThemeContext) || {};
    /*
     * A ABA INICIAL depende do ENDEREÇO (Gisele, 23/10/2026).
     *
     * O link individual do artigo é `.../videos?artigo=94`, e a central de
     * ajuda -- quem sabe abrir um artigo -- vive na aba "Documentos". Sem
     * isto, quem recebe o link cai na aba de vídeos, a central nem chega a
     * montar, e o endereço não abre nada: o link parece quebrado.
     *
     * Descoberto validando em produção. O teste da central montava o
     * componente direto, então o efeito que lê o endereço rodava sempre --
     * e o buraco ficava exatamente entre as duas peças, onde nenhum dos dois
     * testes olhava.
     */
    const [aba, setAba] = useState(() => (
        new URLSearchParams(window.location.search).get('artigo') ? 'documento' : 'video'
    ));
    // Quem mexe na ARVORE da central e administrador -- a api tambem exige.
    const usuario = useSelector((st) => st.user?.currentUser);

    /*
     * QUEM PODE FIXAR (Gisele, 23/10/2026): "criar uma configuracao para
     * definir quem podera fixar os videos: operador ou somente
     * administrador".
     *
     * Virou uma PERMISSAO, e nao uma chave de configuracao, por decisao do
     * dono do produto -- permissao ja tem tela, ja e por perfil e ja e
     * auditavel. Uma chave nova seria um segundo lugar onde se decide quem
     * pode o que, e os dois divergiriam.
     *
     * O comentario do topo deste arquivo dizia que fixar usava a MESMA
     * permissao da base de conhecimento, de proposito, para nao mexer em
     * tres lugares. O pedido dela desfaz essa escolha com razao: fixar
     * decide o que a equipe inteira ve primeiro.
     *
     * Quem GARANTE e a api; este calculo existe para nao oferecer um
     * alfinete que responderia 403.
     */
    const permissoes = useSelector((st) => st.user?.permissions);
    const podeFixar = Boolean(usuario?.is_super_admin)
        || hasAdminProfile(usuario)
        || (Array.isArray(permissoes) ? permissoes : []).includes(PERMISSAO_FIXAR);
    const ehAdministrador = Boolean(usuario?.is_super_admin) || hasAdminProfile(usuario);

    /*
     * QUEM PODE EDITAR (Gisele): "Perfil Líder: editar vídeos" e "Perfil
     * Operador: não permitir editar descrições/conteúdos".
     *
     * ## O botão não tinha portão NENHUM
     *
     * Nem na tela nem na api: `ehAdministrador` guardava só o Apagar, e
     * qualquer operador via o lápis e conseguia salvar. A metade do servidor
     * foi fechada junto com esta (ver `AIDocumentController::update`), porque
     * esconder o botão e deixar a rota aberta é uma trava de mentira.
     *
     * O administrador continua podendo pelo papel: é ele quem concede a
     * permissão, e precisaria dela para dar a si mesmo.
     */
    const podeEditar = ehAdministrador
        || (Array.isArray(permissoes) ? permissoes : []).includes(PERMISSAO_EDITAR);

    /*
     * Voltar para a base só aparece para quem pode ABRIR a base.
     *
     * Desde que as duas telas têm permissões separadas, existe quem veja os
     * vídeos e não veja a IA / Base de Conhecimento -- é o perfil Operador que
     * ela descreveu. Para essa pessoa o botão levaria a uma tela negada.
     */
    const podeVoltarParaABase = (Array.isArray(permissoes) ? permissoes : [])
        .includes('/ia-base-conhecimento');
    const [videos, setVideos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [falhou, setFalhou] = useState(false);
    const [busca, setBusca] = useState('');
    const [categoria, setCategoria] = useState('');
    /*
     * "Somente fixados" (Gisele, 23/10/2026), pedido para as duas abas: nos
     * vídeos como "somente vídeos fixados/destaques" e nos documentos como
     * "filtro para visualizar somente os documentos/manuais fixados". É o
     * mesmo recorte, então é um controle só -- dois seriam duas respostas
     * possíveis para a mesma pergunta na mesma tela.
     */
    const [somenteFixados, setSomenteFixados] = useState(false);
    /*
     * "Meus favoritos" é um recorte SEPARADO de "Somente fixados", e os dois
     * compõem: são perguntas diferentes ("o que a equipe destacou" e "o que
     * eu guardei"), e um controle de três estados obrigaria a escolher entre
     * elas.
     */
    const [somenteFavoritos, setSomenteFavoritos] = useState(false);
    const [favoritando, setFavoritando] = useState(null);
    const [dialogAberto, setDialogAberto] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [fixando, setFixando] = useState(null);
    // Qual material está aberto para edição -- nulo quando é um cadastro novo.
    const [editando, setEditando] = useState(null);
    const [excluindo, setExcluindo] = useState(null);
    const [reordenando, setReordenando] = useState(false);
    const [form, setForm] = useState({ title: '', video_url: '', category: '', content: '' });

    /*
     * O VÍDEO ENVIADO COMO ARQUIVO (dono do produto, 03/11/2026): "aqui também
     * teremos que ter como fazer upload e não somente leitura de youtube ou da
     * plataforma que seja".
     *
     * `menuDeAdicionar` existe porque acrescentar um vídeo passou a ter DUAS
     * origens -- endereço e arquivo. Dois botões lado a lado diriam que são
     * duas coisas diferentes; é a mesma coisa vinda de dois lugares.
     */
    const [menuDeAdicionar, setMenuDeAdicionar] = useState(null);
    /*
     * Os limites vêm do SERVIDOR. Documento e vídeo não têm o mesmo teto, e o
     * do vídeo é configurável por empresa -- escrever os números aqui os faria
     * divergir no dia em que alguém mudasse o limite nas configurações.
     */
    const [limites, setLimites] = useState(null);
    // O material que está tocando, e o endereço de memória do arquivo dele.
    const [tocando, setTocando] = useState(null);

    const textos = ABAS[aba];

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            // O recorte é do SERVIDOR, uma aba por chamada. A listagem completa
            // carrega o conteúdo inteiro de cada documento: trazer a base toda
            // para dividir em duas no navegador seria pagar a transferência
            // inteira para mostrar metade.
            const data = aba === 'video' ? await listVideos() : await listDocumentosDaBase();
            setVideos(Array.isArray(data?.documents) ? data.documents : []);
            setFalhou(false);
        } catch (e) {
            // Lista vazia e lista que não carregou são estados DIFERENTES.
            // Mostrar "nenhum vídeo cadastrado" depois de um erro de rede faz
            // o operador cadastrar de novo o que já existe.
            setFalhou(true);
            setVideos([]);
        } finally {
            setCarregando(false);
        }
        // `aba` entra nas dependências: trocar de aba recarrega do servidor.
    }, [aba]);

    useEffect(() => { carregar(); }, [carregar]);

    /*
     * Os limites uma vez só, na montagem: eles não dependem da aba nem dos
     * filtros, e repetir a chamada a cada troca de aba seria pedir ao servidor
     * uma resposta que não muda.
     */
    useEffect(() => {
        let vivo = true;

        limitesDaBase().then((novos) => {
            if (vivo) setLimites(novos);
        });

        return () => { vivo = false; };
    }, []);

    /**
     * Fixar e desfixar o material no topo da aba.
     *
     * Reordena na tela na hora e NÃO recarrega: a lista já está certa (fixado
     * vem primeiro, e dentro do grupo vale a ordem de sempre), e uma recarga
     * faria a página piscar inteira para mover um cartão.
     *
     * Falha devolve o cartão ao lugar de antes. Um alfinete que aparece aceso
     * sem ter salvado é pior que um erro: a pessoa sai achando que fixou.
     */
    const alternarFixado = async (material) => {
        const novo = !material.fixado;
        setFixando(material.id);
        try {
            await updateDocument(material.id, { fixado: novo });
            setVideos((atuais) => ordenarComFixadosPrimeiro(
                atuais.map((m) => (m.id === material.id ? { ...m, fixado: novo } : m))
            ));
            toast.success(
                novo ? 'Fixado no topo.' : 'Desfixado.',
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.error || 'Não foi possível fixar o material.',
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } finally {
            setFixando(null);
        }
    };

    /**
     * Favoritar e desfavoritar — o material fica em destaque NA TELA DELE.
     *
     * ## Não é o mesmo botão que o alfinete, de propósito
     *
     * O alfinete é o destaque institucional: o Admin/Líder marca e a equipe
     * inteira vê aquilo em primeiro. A Gisele pediu as duas coisas na mesma
     * frase — "o operador poder fixar os favoritos dele" e "o operador não
     * poder desfixar o que o Admin fixou" —, e dar a permissão de fixar ao
     * operador faria exatamente o que a segunda metade proíbe.
     *
     * Por isso são dois controles: o alfinete (com permissão) e a estrela
     * (sem, porque só mexe na tela de quem clicou).
     *
     * Reordena na hora e não recarrega, como o alfinete. Falha devolve o
     * cartão ao lugar: uma estrela acesa sem ter salvado faz a pessoa sair
     * achando que guardou.
     */
    const alternarFavorito = async (material) => {
        const novo = !material.favorito;
        setFavoritando(material.id);
        try {
            if (novo) {
                await favoritarMaterial(material.id);
            } else {
                await desfavoritarMaterial(material.id);
            }

            setVideos((atuais) => ordenarComFixadosPrimeiro(
                atuais.map((m) => (m.id === material.id ? { ...m, favorito: novo } : m))
            ));
        } catch (e) {
            toast.error(
                e?.response?.data?.error || 'Não foi possível salvar o favorito.',
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } finally {
            setFavoritando(null);
        }
    };

    /**
     * Enviar documento (a aba de documentos).
     *
     * O título sai do nome do arquivo, no servidor — é o que a tela de
     * administração da base já faz, e pedir título antes de escolher o arquivo
     * seria atrito para repetir o que o nome do arquivo já diz. Categoria,
     * tags e descrição se ajustam depois, na base de conhecimento.
     */
    const enviarArquivo = async (arquivo, ehVideo = false) => {
        if (!arquivo) return;

        const palavra = ehVideo ? 'vídeo' : 'documento';

        /*
         * Confere ANTES de enviar.
         *
         * Sem isto o arquivo sobe INTEIRO para ser recusado no fim -- e num
         * vídeo de 300 MB isso são minutos antes de uma mensagem genérica.
         * Foi este o defeito que originou a política de anexos: o front do
         * chat deixava passar 21 MB e o servidor recusava a partir de 10.
         *
         * Quem GARANTE continua sendo o servidor; isto é para a pessoa saber
         * agora.
         */
        const recusa = recusaDoArquivo(arquivo, limites?.[ehVideo ? 'video' : 'documento']);
        if (recusa) {
            toast.error(recusa, { position: toast.POSITION.BOTTOM_CENTER });
            return;
        }

        const dados = new FormData();
        dados.append('file', arquivo);

        setSalvando(true);
        try {
            await uploadDocument(dados);
            toast.success(
                ehVideo ? 'Vídeo enviado à base.' : 'Documento enviado à base.',
                { position: toast.POSITION.BOTTOM_CENTER }
            );
            carregar();
        } catch (e) {
            const resposta = e?.response?.data;
            const jaExiste = resposta?.ja_existe;
            // A api devolve o motivo em `errors.file` quando é tamanho ou tipo;
            // `error` é a mensagem geral. Preferir a específica é o que
            // diferencia "arquivo grande demais" de "não deu certo".
            const doArquivo = resposta?.errors?.file?.[0];

            toast.error(
                jaExiste
                    ? `Este ${palavra} já está na base: "${jaExiste.title}".`
                    : (doArquivo || resposta?.error || `Não foi possível enviar o ${palavra}.`),
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } finally {
            setSalvando(false);
        }
    };

    /**
     * Tocar um vídeo ENVIADO como arquivo.
     *
     * O `<video>` não pode apontar direto para a rota: ela é autenticada por
     * JWT no cabeçalho e o player busca o endereço sozinho, sem cabeçalho
     * nenhum -- responderia 401. Então o arquivo vem pelo serviço, com o
     * token, e o player recebe um endereço de memória.
     */
    const tocar = async (material) => {
        setTocando({ material, url: null });

        try {
            const url = await urlDoArquivoDoMaterial(material);
            // A pessoa pode ter fechado enquanto baixava: abrir o player
            // depois disso jogaria um vídeo na tela sem ninguém ter pedido.
            setTocando((atual) => {
                if (atual?.material?.id !== material.id) {
                    URL.revokeObjectURL(url);
                    return atual;
                }
                return { material, url };
            });
        } catch (e) {
            setTocando(null);
            toast.error('Não foi possível carregar o vídeo.', { position: toast.POSITION.BOTTOM_CENTER });
        }
    };

    const fecharPlayer = () => {
        // Sem o `revoke` o vídeo fica na memória da aba até alguém recarregar
        // a página -- e são centenas de MB por vídeo aberto.
        if (tocando?.url) {
            URL.revokeObjectURL(tocando.url);
        }
        setTocando(null);
    };

    const baixar = async (material) => {
        try {
            await downloadDocument(material);
        } catch (e) {
            toast.error('Não foi possível baixar o documento.', { position: toast.POSITION.BOTTOM_CENTER });
        }
    };

    const categorias = useMemo(() => {
        const vistas = new Set();
        videos.forEach((v) => vistas.add(v.category || SEM_CATEGORIA));
        return Array.from(vistas).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [videos]);

    const visiveis = useMemo(() => {
        const termo = busca.trim().toLowerCase();

        return videos.filter((v) => {
            // Os filtros COMPÕEM: ligar os dois recorta a interseção. Cada um
            // responde "não" sozinho e nenhum escreve por cima do outro.
            if (somenteFixados && !v.fixado) return false;
            if (somenteFavoritos && !v.favorito) return false;

            const daCategoria = categoria === '' || (v.category || SEM_CATEGORIA) === categoria;
            if (!daCategoria) return false;
            if (termo === '') return true;

            // Busca no que a pessoa LÊ no cartão mais as palavras-chave, que
            // são o campo feito para encontrar o material por outro nome.
            return [v.title, v.category, v.keywords, (v.tags || []).join(' ')]
                .filter(Boolean)
                .some((campo) => String(campo).toLowerCase().includes(termo));
        });
    }, [videos, busca, categoria, somenteFixados, somenteFavoritos]);

    const abrirDialog = () => {
        setEditando(null);
        setForm({ title: '', video_url: '', category: '', content: '' });
        setDialogAberto(true);
    };

    /**
     * Abrir o mesmo diálogo para EDITAR (Gisele, 23/10/2026).
     *
     * "Ao localizar um vídeo, disponibilizar as opções: Editar; Inativar /
     * Excluir -- somente para administrador; Copiar link do vídeo."
     *
     * É o formulário de cadastro com os campos preenchidos, e não uma tela
     * nova: os campos são os mesmos, e dois formulários para o mesmo registro
     * divergem no primeiro campo que alguém adicionar num só deles.
     */
    const editar = (material) => {
        setEditando(material);
        setForm({
            title: material.title || '',
            video_url: material.video_url || '',
            category: material.category || '',
            content: material.content || '',
        });
        setDialogAberto(true);
    };

    /**
     * Apagar o material. Só administrador.
     *
     * A api é quem GARANTE (a rota exige `admin`); aqui o botão some porque
     * oferecer uma ação que vai voltar 403 é pior do que não oferecer.
     *
     * Apagar o documento apaga também o que ele indexou no Qdrant -- quem faz
     * isso é a api, no mesmo caminho que a tela de documentos já usa.
     */
    const excluir = async (material) => {
        // A confirmação é do navegador de propósito: é a mesma que a tela de
        // documentos usa, e um diálogo próprio aqui seria uma terceira forma
        // de perguntar a mesma coisa no mesmo sistema.
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Apagar "${material.title}" da base? O material sai também das buscas do Orby.`)) {
            return;
        }

        setExcluindo(material.id);
        try {
            await deleteDocument(material.id);
            setVideos((atuais) => atuais.filter((m) => m.id !== material.id));
            toast.success('Material apagado.', { position: toast.POSITION.BOTTOM_CENTER });
        } catch (e) {
            toast.error(
                e?.response?.data?.error || 'Não foi possível apagar o material.',
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } finally {
            setExcluindo(null);
        }
    };

    /**
     * Copiar o endereço do vídeo.
     *
     * `navigator.clipboard` não existe fora de https (e nem em todo navegador
     * antigo). Sem o plano B o botão ficaria mudo justamente em quem mais
     * precisa de ajuda -- por isso o `textarea` escondido.
     */
    /**
     * Mudar a ordem dos fixados (Gisele, 23/10/2026).
     *
     * A conta mora em `utils/ordemDosFixados`, com teste: qual é o vizinho
     * dentro do grupo, quem precisa de `order` novo, e por que o não fixado
     * nunca entra.
     *
     * A tela muda PRIMEIRO e a api depois. Esperar as duas gravações deixaria
     * o cartão parado sob o dedo de quem clicou; e se a gravação falhar, a
     * lista volta ao que era -- mostrar uma ordem que o servidor não tem é
     * pior que não reordenar.
     */
    const mover = async (material, direcao) => {
        const anterior = videos;
        const { lista, gravar } = reordenarFixados(videos, material.id, direcao);

        if (gravar.length === 0) return;

        setVideos(lista);
        setReordenando(true);
        try {
            // Em sequência, e não em paralelo: são duas ou três linhas, e o
            // `Promise.all` só esconderia qual delas falhou.
            for (const alvo of gravar) {
                // eslint-disable-next-line no-await-in-loop
                await updateDocument(alvo.id, { order: alvo.order });
            }
        } catch (e) {
            setVideos(anterior);
            toast.error(
                e?.response?.data?.error || 'Não foi possível mudar a ordem.',
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } finally {
            setReordenando(false);
        }
    };

    const copiarLink = async (material) => {
        const url = String(material.video_url || '').trim();

        if (url === '') {
            toast.error('Este material não tem endereço para copiar.', { position: toast.POSITION.BOTTOM_CENTER });
            return;
        }

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                const caixa = document.createElement('textarea');
                caixa.value = url;
                document.body.appendChild(caixa);
                caixa.select();
                document.execCommand('copy');
                document.body.removeChild(caixa);
            }
            toast.success('Link copiado.', { position: toast.POSITION.BOTTOM_CENTER });
        } catch (e) {
            toast.error('Não foi possível copiar o link.', { position: toast.POSITION.BOTTOM_CENTER });
        }
    };

    const salvar = async () => {
        const titulo = form.title.trim();
        const url = form.video_url.trim();

        if (titulo === '' || url === '') {
            toast.error('Informe o título e o endereço do vídeo.', { position: toast.POSITION.BOTTOM_CENTER });
            return;
        }

        setSalvando(true);
        try {
            const campos = {
                title: titulo,
                video_url: url,
                category: form.category.trim() || null,
                // A api exige conteúdo, e é ele que vai para o Qdrant. Sem
                // descrição o título é o que sobra para o Orby casar a
                // pergunta -- melhor que recusar o cadastro por isso.
                content: form.content.trim() || titulo,
            };

            if (editando) {
                await updateDocument(editando.id, campos);
                toast.success('Vídeo atualizado.', { position: toast.POSITION.BOTTOM_CENTER });
            } else {
                await createDocument(campos);
                toast.success('Vídeo adicionado à base.', { position: toast.POSITION.BOTTOM_CENTER });
            }

            setDialogAberto(false);
            setEditando(null);
            carregar();
        } catch (e) {
            // 409 é o vídeo que já está na base, e a api diz QUAL. Repetir a
            // mensagem genérica aqui desperdiçaria a única informação que
            // resolve o caso.
            const resposta = e?.response?.data;
            const jaExiste = resposta?.ja_existe;

            toast.error(
                jaExiste
                    ? `Este vídeo já está na base: "${jaExiste.title}".`
                    : (resposta?.error || 'Não foi possível adicionar o vídeo.'),
                { position: toast.POSITION.BOTTOM_CENTER }
            );
        } finally {
            setSalvando(false);
        }
    };

    const corTexto = theme?.palette?.text?.primary;
    const corSecundaria = theme?.palette?.text?.secondary;

    return (
        <Box sx={{ p: 3, backgroundColor: theme?.palette?.background?.default, minHeight: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {podeVoltarParaABase && (
                    <IconButton component={Link} to="/ia-base-conhecimento" aria-label="Voltar para a base de conhecimento">
                        <ArrowBack sx={{ color: corTexto }} />
                    </IconButton>
                )}
                <Typography variant="h6" sx={{ color: corTexto, flexGrow: 1 }}>
                    Vídeos e treinamentos
                </Typography>
                <Tooltip title="Recarregar">
                    <IconButton onClick={carregar} aria-label="Recarregar">
                        <Refresh sx={{ color: corTexto }} />
                    </IconButton>
                </Tooltip>
                {/*
                    O botão é o da ABA: vídeo se cadastra por endereço, documento
                    se envia como arquivo. Um botão só, que mudasse de
                    comportamento sem mudar de nome, faria a pessoa procurar o
                    campo de arquivo dentro do formulário de vídeo.
                */}
                {/*
                    ADICIONAR segue a MESMA permissão de editar (decisão do dono
                    do produto, 03/11/2026): quem mantém a base corrige o que
                    está lá e acrescenta o que falta. Uma permissão separada
                    seria uma caixa a mais na tela para uma distinção que
                    ninguém pediu.
                */}
                {!podeEditar ? null : aba === 'video' ? (
                    <>
                        {/*
                            UM botão com duas origens, e não dois botões lado a
                            lado: acrescentar um vídeo é uma coisa só -- o que
                            muda é de onde ele vem. Dois botões diriam que são
                            duas funções diferentes da tela.
                        */}
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={({ currentTarget }) => setMenuDeAdicionar(currentTarget)}
                            disabled={salvando}
                            data-testid="adicionar-video"
                        >
                            {salvando ? 'Enviando...' : 'Adicionar vídeo'}
                        </Button>
                        <Menu
                            anchorEl={menuDeAdicionar}
                            open={Boolean(menuDeAdicionar)}
                            onClose={() => setMenuDeAdicionar(null)}
                        >
                            <MenuItem
                                onClick={() => { setMenuDeAdicionar(null); abrirDialog(); }}
                                data-testid="adicionar-video-por-endereco"
                            >
                                <ListItemIcon><LinkDeVideo fontSize="small" /></ListItemIcon>
                                <ListItemText
                                    primary="Pelo endereço"
                                    secondary="YouTube, Vimeo ou outro"
                                />
                            </MenuItem>
                            <MenuItem component="label" data-testid="adicionar-video-por-arquivo">
                                <ListItemIcon><VideoFile fontSize="small" /></ListItemIcon>
                                <ListItemText
                                    primary="Enviar arquivo"
                                    secondary={
                                        limites?.video?.limite_mb
                                            ? `${EXTENSOES_DE_VIDEO.join(', ')} — até ${limites.video.limite_mb} MB`
                                            : EXTENSOES_DE_VIDEO.join(', ')
                                    }
                                />
                                <input
                                    type="file"
                                    hidden
                                    accept={aceitar(EXTENSOES_DE_VIDEO)}
                                    data-testid="arquivo-do-video"
                                    onChange={({ target }) => {
                                        const arquivo = target.files?.[0];
                                        // Limpa o input: sem isto, enviar o MESMO
                                        // arquivo duas vezes seguidas não dispararia
                                        // o segundo `onChange` -- o valor não teria
                                        // mudado.
                                        target.value = '';
                                        setMenuDeAdicionar(null);
                                        enviarArquivo(arquivo, true);
                                    }}
                                />
                            </MenuItem>
                        </Menu>
                    </>
                ) : (
                    <Button
                        variant="contained"
                        component="label"
                        startIcon={<UploadFile />}
                        disabled={salvando}
                    >
                        {salvando ? 'Enviando...' : 'Enviar documento'}
                        <input
                            type="file"
                            hidden
                            accept={aceitar(EXTENSOES_DE_DOCUMENTO)}
                            data-testid="arquivo-do-documento"
                            onChange={({ target }) => {
                                const arquivo = target.files?.[0];
                                // Limpa o input: sem isto, enviar o MESMO arquivo
                                // duas vezes seguidas não disparia o segundo
                                // `onChange` -- o valor não teria mudado.
                                target.value = '';
                                enviarArquivo(arquivo);
                            }}
                        />
                    </Button>
                )}
            </Box>

            <Tabs
                value={aba}
                onChange={(_, nova) => {
                    setAba(nova);
                    // Os filtros são da aba que estava aberta: manter a busca de
                    // "nota fiscal" ao trocar para documentos mostraria uma aba
                    // vazia que parece não ter nada cadastrado.
                    setBusca('');
                    setCategoria('');
                }}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label={ABAS.video.titulo} value="video" data-testid="aba-videos" />
                <Tab label={ABAS.documento.titulo} value="documento" data-testid="aba-documentos" />
            </Tabs>

            <Typography variant="body2" sx={{ color: corSecundaria, mb: 2 }}>
                {aba === 'video'
                    ? 'Os vídeos daqui são a mesma base de conhecimento dos manuais, e o ChatOrby responde com eles.'
                    : 'Os documentos daqui são a mesma base de conhecimento dos vídeos, e o ChatOrby responde com eles.'}
            </Typography>

            {/*
                A aba de DOCUMENTOS virou a central de ajuda (20/10): arvore de
                secoes, lista paginada do servidor e o artigo aberto na tela.
                Ela tem busca, filtro e acoes proprias, entao a barra de filtros e a
                grade de cartoes abaixo ficam so com os VIDEOS -- o contrario seria
                dois filtros concorrentes na mesma tela, um deles mentindo.
            */}
            {aba === 'documento' ? (
                <CentralDeAjuda podeAdministrar={ehAdministrador} podeFixar={podeFixar} />
            ) : (
            <>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Buscar por título, categoria ou palavra-chave"
                    value={busca}
                    onChange={({ target }) => setBusca(target.value)}
                    sx={{ minWidth: 320, flexGrow: 1 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                    }}
                    inputProps={{ 'data-testid': 'busca-de-videos' }}
                />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                        value={categoria}
                        label="Categoria"
                        onChange={({ target }) => setCategoria(target.value)}
                        inputProps={{ 'data-testid': 'categoria-de-videos' }}
                    >
                        <MenuItem value="">Todas as categorias</MenuItem>
                        {categorias.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                </FormControl>
                {/*
                  * "Somente fixados" é um BOTÃO QUE FICA APERTADO, e não uma
                  * caixa de seleção: ele muda o que a lista mostra, e precisa
                  * se denunciar de longe. Filtro ligado que não parece ligado
                  * é o caminho mais curto para "sumiram meus vídeos".
                  */}
                <Button
                    size="small"
                    variant={somenteFixados ? 'contained' : 'outlined'}
                    startIcon={somenteFixados ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
                    onClick={() => setSomenteFixados((ligado) => !ligado)}
                    aria-pressed={somenteFixados}
                    data-testid="somente-fixados"
                    sx={{ flexShrink: 0 }}
                >
                    Somente fixados
                </Button>

                {/*
                  * "Meus favoritos" é SEPARADO de "Somente fixados", e os dois
                  * compõem. São perguntas diferentes -- "o que a equipe
                  * destacou" e "o que eu guardei" --, e um controle de três
                  * estados obrigaria a escolher entre elas.
                  *
                  * O possessivo no rótulo não é enfeite: é o que diz, sem
                  * explicação, que este recorte é pessoal e o outro não.
                  */}
                <Button
                    size="small"
                    variant={somenteFavoritos ? 'contained' : 'outlined'}
                    startIcon={somenteFavoritos ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                    onClick={() => setSomenteFavoritos((ligado) => !ligado)}
                    aria-pressed={somenteFavoritos}
                    data-testid="somente-favoritos"
                    sx={{ flexShrink: 0 }}
                >
                    Meus favoritos
                </Button>
            </Box>

            {carregando && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {!carregando && falhou && (
                <Typography sx={{ color: corSecundaria, py: 4 }}>
                    {textos.falhou}
                </Typography>
            )}

            {/* Os dois vazios são mensagens DIFERENTES de propósito: trocar uma
                pela outra manda o operador procurar um filtro que não é a
                causa, ou faz parecer que a base esvaziou. Mesma decisão do
                estado vazio da lista de conversas (item 6). */}
            {!carregando && !falhou && videos.length === 0 && (
                <Typography sx={{ color: corSecundaria, py: 4 }}>
                    {textos.vazio}
                </Typography>
            )}

            {!carregando && !falhou && videos.length > 0 && visiveis.length === 0 && (
                <Typography sx={{ color: corSecundaria, py: 4 }}>
                    {textos.semFiltro}
                </Typography>
            )}

            <Grid container spacing={2}>
                {visiveis.map((v) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={v.id}>
                        <Card sx={{
                            height: '100%',
                            /*
                             * COLUNA, e não bloco (defeito achado em produção
                             * em 26/10/2026).
                             *
                             * A área clicável tem `height: 100%` e ocupava o
                             * cartão inteiro; a barra de ações (editar, copiar
                             * link, apagar) sobrava 28px ABAIXO do fim do
                             * cartão, e o `overflow: hidden` a cortava. Os
                             * botões existiam no DOM -- o teste os achava pelo
                             * `data-testid` e conseguia clicar -- mas ninguém
                             * os via na tela.
                             *
                             * Em coluna, a área clicável cresce no espaço que
                             * sobra (`flexGrow`) e a barra fica DENTRO do
                             * cartão, embaixo dela.
                             *
                             * O jsdom não calcula layout, então nenhum teste
                             * daqui pega este tipo de defeito: quem pega é
                             * abrir a tela.
                             */
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: theme?.palette?.background?.paper,
                            // `relative` para o alfinete: ver abaixo.
                            position: 'relative',
                            ...(v.fixado ? {
                                // O fixado se reconhece sem ler: ele está em
                                // primeiro, e a borda diz por quê. Cor da paleta
                                // e não hex -- um hex fixo ficaria ilegível em
                                // um dos dois temas.
                                border: '1px solid',
                                borderColor: 'primary.main',
                            } : null),
                        }}>
                            {/*
                                O alfinete fica FORA da área clicável do cartão.
                                Dentro dela ele herdaria o link: fixar abriria o
                                vídeo numa aba nova no mesmo clique.
                            */}
                            {/*
                              * O ALFINETE SÓ PARA QUEM PODE FIXAR (Gisele,
                              * 23/10/2026). O alfinete some, e não fica
                              * desligado: desligado ele continuaria dizendo
                              * "este material está fixado" para quem não pode
                              * mexer, e essa informação já está na borda do
                              * cartão e na posição dele na lista.
                              */}
                            {podeFixar && (
                            <Tooltip title={v.fixado ? 'Desfixar do topo' : 'Fixar no topo'}>
                                <IconButton
                                    size="small"
                                    onClick={() => alternarFixado(v)}
                                    disabled={fixando === v.id}
                                    aria-label={v.fixado ? `Desfixar ${v.title}` : `Fixar ${v.title} no topo`}
                                    data-testid={`fixar-${v.id}`}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        zIndex: 2,
                                        backgroundColor: theme?.palette?.background?.paper,
                                        '&:hover': { backgroundColor: theme?.palette?.action?.hover },
                                    }}
                                >
                                    {v.fixado
                                        ? <PushPin sx={{ fontSize: 16, color: 'primary.main' }} />
                                        : <PushPinOutlined sx={{ fontSize: 16, color: corSecundaria }} />}
                                </IconButton>
                            </Tooltip>
                            )}

                            {/*
                              * A ESTRELA — o favorito DELE (Gisele, 23/10/2026).
                              *
                              * Sem permissão e para todo mundo: ela muda a ordem
                              * na tela de quem clicou, e só dela. O alfinete, ao
                              * lado, é o destaque da equipe e por isso tem porta.
                              *
                              * Fica à ESQUERDA do alfinete, e não no lugar dele:
                              * um material pode ser as duas coisas ao mesmo
                              * tempo, e quem tem as duas precisa ver as duas.
                              * Quando o alfinete não aparece, a estrela ocupa o
                              * canto sozinha -- `right` acompanha.
                              */}
                            <Tooltip title={v.favorito ? 'Tirar dos meus favoritos' : 'Guardar nos meus favoritos'}>
                                <IconButton
                                    size="small"
                                    onClick={() => alternarFavorito(v)}
                                    disabled={favoritando === v.id}
                                    aria-label={v.favorito
                                        ? `Tirar ${v.title} dos meus favoritos`
                                        : `Guardar ${v.title} nos meus favoritos`}
                                    data-testid={`favoritar-${v.id}`}
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: podeFixar ? 36 : 4,
                                        zIndex: 2,
                                        backgroundColor: theme?.palette?.background?.paper,
                                        '&:hover': { backgroundColor: theme?.palette?.action?.hover },
                                    }}
                                >
                                    {v.favorito
                                        ? <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                                        : <StarBorder sx={{ fontSize: 16, color: corSecundaria }} />}
                                </IconButton>
                            </Tooltip>

                            {aba === 'documento' ? (
                                /*
                                  O cartão do documento: sem miniatura (não há o
                                  que mostrar) e com "Baixar" em vez de "abrir em
                                  nova aba". Documento criado como texto, sem
                                  arquivo, não oferece download -- um botão que
                                  responde 404 é pior que nenhum botão.
                                */
                                <CardActionArea
                                    onClick={() => baixar(v)}
                                    disabled={!(v.file_path || v.original_filename)}
                                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                                    data-testid={`documento-${v.id}`}
                                >
                                    <Box sx={{
                                        width: '100%', aspectRatio: '16 / 9', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: theme?.palette?.action?.hover || 'rgba(127,127,127,0.12)',
                                    }}>
                                        <DescriptionOutlined sx={{ fontSize: 48, color: corSecundaria }} />
                                    </Box>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography variant="subtitle2" sx={{ color: corTexto }}>
                                            {v.title}
                                        </Typography>
                                        {v.category && (
                                            <Chip size="small" label={v.category} sx={{ mt: 1 }} />
                                        )}
                                        {(v.file_path || v.original_filename) && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                                <Download sx={{ fontSize: 14, color: corSecundaria }} />
                                                <Typography variant="caption" sx={{ color: corSecundaria }}>
                                                    Baixar
                                                </Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </CardActionArea>
                            ) : ehVideoEnviado(v) ? (
                                /*
                                  O VÍDEO ENVIADO COMO ARQUIVO (03/11/2026).
                                  Ele não tem endereço para abrir numa aba --
                                  o cartão de link desenharia `href=""`, e
                                  clicar recarregaria a página. Toca aqui.
                                */
                                <CardActionArea
                                    onClick={() => tocar(v)}
                                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                                    data-testid={`video-enviado-${v.id}`}
                                >
                                    <Box sx={{
                                        width: '100%', aspectRatio: '16 / 9', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: theme?.palette?.action?.hover || 'rgba(127,127,127,0.12)',
                                    }}>
                                        <PlayCircleOutline sx={{ fontSize: 48, color: corSecundaria }} />
                                    </Box>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography variant="subtitle2" sx={{ color: corTexto }}>
                                            {v.title}
                                        </Typography>
                                        {v.category && (
                                            <Chip size="small" label={v.category} sx={{ mt: 1 }} />
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                            <PlayArrow sx={{ fontSize: 14, color: corSecundaria }} />
                                            <Typography variant="caption" sx={{ color: corSecundaria }}>
                                                Assistir
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </CardActionArea>
                            ) : (
                            <CardActionArea
                                component="a"
                                href={v.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                                data-testid={`video-${v.id}`}
                            >
                                {/* Miniatura só quando o servidor soube qual é.
                                    Imagem quebrada é pior que bloco sem imagem,
                                    e o Vimeo exigiria chamada de api para
                                    descobrir a dele. */}
                                {v.video_thumbnail ? (
                                    <Box
                                        component="img"
                                        src={v.video_thumbnail}
                                        alt=""
                                        sx={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }}
                                    />
                                ) : (
                                    <Box sx={{
                                        width: '100%', aspectRatio: '16 / 9', display: 'flex',
                                        alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: theme?.palette?.action?.hover || 'rgba(127,127,127,0.12)',
                                    }}>
                                        <PlayCircleOutline sx={{ fontSize: 48, color: corSecundaria }} />
                                    </Box>
                                )}

                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle2" sx={{ color: corTexto }}>
                                        {v.title}
                                    </Typography>
                                    {v.category && (
                                        <Chip size="small" label={v.category} sx={{ mt: 1 }} />
                                    )}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                        <OpenInNew sx={{ fontSize: 14, color: corSecundaria }} />
                                        <Typography variant="caption" sx={{ color: corSecundaria }}>
                                            Abrir em nova aba
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                            )}

                            {/*
                              * As ações do material (Gisele, 23/10/2026).
                              *
                              * FORA do `CardActionArea`: dentro dele, qualquer
                              * clique herdaria o link e "Editar" abriria o
                              * vídeo numa aba nova -- o mesmo motivo pelo qual
                              * o alfinete fica de fora.
                              */}
                            <Box sx={{
                                display: 'flex', gap: 0.5, px: 1, py: 0.5,
                                borderTop: '1px solid', borderColor: 'divider',
                            }}>
                                {/*
                                  * Subir e descer só aparecem no FIXADO: a
                                  * ordem que eles mudam é a do topo, e num
                                  * material comum os botões não teriam efeito
                                  * visível nenhum -- um botão que não faz nada
                                  * é pior que um botão que não existe.
                                  *
                                  * Desligados nas pontas, em vez de escondidos:
                                  * sumir faria o primeiro e o último parecerem
                                  * não reordenáveis.
                                  */}
                                {/* Mudar a ordem dos fixados é a MESMA
                                    autoridade de fixar: as duas decidem o que a
                                    equipe inteira vê primeiro. */}
                                {v.fixado && podeFixar && (
                                    <>
                                        <Tooltip title="Subir">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => mover(v, 'cima')}
                                                    disabled={reordenando || !podeSubir(visiveis, v.id)}
                                                    aria-label={`Subir ${v.title}`}
                                                    data-testid={`subir-${v.id}`}
                                                >
                                                    <ArrowUpward sx={{ fontSize: 16, color: corSecundaria }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Descer">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => mover(v, 'baixo')}
                                                    disabled={reordenando || !podeDescer(visiveis, v.id)}
                                                    aria-label={`Descer ${v.title}`}
                                                    data-testid={`descer-${v.id}`}
                                                >
                                                    <ArrowDownward sx={{ fontSize: 16, color: corSecundaria }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </>
                                )}

                                {podeEditar && (
                                    <Tooltip title="Editar">
                                        <IconButton
                                            size="small"
                                            onClick={() => editar(v)}
                                            aria-label={`Editar ${v.title}`}
                                            data-testid={`editar-${v.id}`}
                                        >
                                            <EditOutlined sx={{ fontSize: 16, color: corSecundaria }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* O vídeo enviado se baixa, como o documento:
                                    o cartão dele toca, então sem isto não
                                    haveria como guardar o arquivo. */}
                                {ehVideoEnviado(v) && (
                                    <Tooltip title="Baixar">
                                        <IconButton
                                            size="small"
                                            onClick={() => baixar(v)}
                                            aria-label={`Baixar ${v.title}`}
                                            data-testid={`baixar-${v.id}`}
                                        >
                                            <Download sx={{ fontSize: 16, color: corSecundaria }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {/* Copiar link só faz sentido onde HÁ link: o
                                    documento enviado como arquivo não tem um,
                                    e um botão que avisa "não tem endereço"
                                    seria um botão para dar má notícia. */}
                                {v.video_url && (
                                    <Tooltip title="Copiar link">
                                        <IconButton
                                            size="small"
                                            onClick={() => copiarLink(v)}
                                            aria-label={`Copiar link de ${v.title}`}
                                            data-testid={`copiar-link-${v.id}`}
                                        >
                                            <LinkOutlined sx={{ fontSize: 16, color: corSecundaria }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                <Box sx={{ flexGrow: 1 }} />

                                {ehAdministrador && (
                                    <Tooltip title="Apagar">
                                        <IconButton
                                            size="small"
                                            onClick={() => excluir(v)}
                                            disabled={excluindo === v.id}
                                            aria-label={`Apagar ${v.title}`}
                                            data-testid={`excluir-${v.id}`}
                                        >
                                            <DeleteOutline sx={{ fontSize: 16, color: corSecundaria }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            </>
            )}

            <Dialog
                open={dialogAberto}
                onClose={() => { setDialogAberto(false); setEditando(null); }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{editando ? 'Editar vídeo' : 'Adicionar vídeo'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth margin="normal" label="Título" required
                        value={form.title}
                        onChange={({ target }) => setForm((f) => ({ ...f, title: target.value }))}
                        inputProps={{ 'data-testid': 'titulo-do-video' }}
                    />
                    <TextField
                        fullWidth margin="normal" label="Endereço do vídeo" required
                        placeholder="https://www.youtube.com/watch?v=..."
                        value={form.video_url}
                        onChange={({ target }) => setForm((f) => ({ ...f, video_url: target.value }))}
                        helperText="YouTube, Vimeo ou qualquer endereço http(s)."
                        inputProps={{ 'data-testid': 'url-do-video' }}
                    />
                    <TextField
                        fullWidth margin="normal" label="Categoria"
                        value={form.category}
                        onChange={({ target }) => setForm((f) => ({ ...f, category: target.value }))}
                        inputProps={{ 'data-testid': 'categoria-do-video' }}
                    />
                    <TextField
                        fullWidth margin="normal" label="Descrição" multiline minRows={3}
                        value={form.content}
                        onChange={({ target }) => setForm((f) => ({ ...f, content: target.value }))}
                        helperText="É por ela que o ChatOrby encontra o vídeo. Sem descrição, vale o título."
                        inputProps={{ 'data-testid': 'descricao-do-video' }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => { setDialogAberto(false); setEditando(null); }} disabled={salvando}>Cancelar</Button>
                    <Button variant="contained" onClick={salvar} disabled={salvando}>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {salvando ? 'Salvando...' : (editando ? 'Salvar' : 'Adicionar')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/*
              O PLAYER do vídeo enviado como arquivo (03/11/2026).

              Num diálogo, e não numa página própria: assistir aqui é para
              CONFERIR o que se acabou de subir, e sair do player devolve a
              pessoa à lista com os filtros e a rolagem onde estavam.

              Quem assiste de verdade é o cliente, pela página pública -- e lá
              a rota responde em pedaços (`Range`), sem precisar baixar o
              arquivo inteiro antes do primeiro segundo.
            */}
            <Dialog open={Boolean(tocando)} onClose={fecharPlayer} maxWidth="md" fullWidth>
                <DialogTitle sx={{ color: corTexto }}>{tocando?.material?.title}</DialogTitle>
                <DialogContent>
                    {tocando?.url ? (
                        <Box
                            component="video"
                            controls
                            autoPlay
                            src={tocando.url}
                            data-testid="player-do-video"
                            sx={{ width: '100%', maxHeight: '70vh', display: 'block', backgroundColor: '#000' }}
                        />
                    ) : (
                        /*
                          O arquivo vem inteiro antes de tocar -- ver
                          `urlDoArquivoDoMaterial`. Sem este aviso a pessoa
                          olharia para um diálogo vazio achando que quebrou.
                        */
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
                            <CircularProgress />
                            <Typography variant="body2" sx={{ color: corSecundaria }}>
                                Carregando o vídeo...
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={fecharPlayer}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VideosDaBasePage;
